import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { useKeenSlider } from 'keen-slider/react';
import { useCallback, useEffect, useRef, useState, } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { LoadingIcon } from '@/components/Icons/LoadingIcon';
import { OldSendMessageIcon } from '@/components/Icons/OldSendMessageIcon';
import { MAX_CHARS, MAX_TYPING_DURATION_MS } from '@/config/constants';
import { useDebounce } from '@/hooks/useDebounce';
import { useFileDropzone } from '@/hooks/useFileDropzone';
import { useSocket } from '@/hooks/useSocket';
import { canUploadFileSelector, themeSelector, widgetPositionSelector, } from '@/state/config';
import { disableInputSelector, filesToUploadLengthSelector, filesToUploadState, isMobileSelector, isUploadingFileState, isWidgetOpenState, } from '@/state/session';
import { connectionState } from '@/state/socket';
import { getFileTypeFromMimeType, KEYS } from '@/util/utils';
import { File as FileIcon } from '../Icons/File';
import { FilesCarousel } from './FilesCarousel/FilesCarousel';
import styles from './MessageInput.module.scss';
export const MessageInput = () => {
    const { t } = useTranslation();
    const theme = useRecoilValue(themeSelector);
    // useState
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [disableInput, setDisableInput] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slidesWidth, setSlidesWidth] = useState(0);
    // useRef
    const inputRef = useRef(null);
    //useRecoilState
    const [filesToUpload, setFilesToUpload] = useRecoilState(filesToUploadState);
    // useRecoilValue
    const connectionStatus = useRecoilValue(connectionState);
    const shouldDisableInput = useRecoilValue(disableInputSelector);
    const isWidgetOpen = useRecoilValue(isWidgetOpenState);
    const isMobile = useRecoilValue(isMobileSelector);
    const filesToUploadLength = useRecoilValue(filesToUploadLengthSelector);
    const canUploadFile = useRecoilValue(canUploadFileSelector);
    const isUploading = useRecoilValue(isUploadingFileState);
    const { sendMessage, sendCompose } = useSocket();
    // useDebounce
    const debouncedInput = useDebounce(String(input), MAX_TYPING_DURATION_MS);
    const widgetPosition = useRecoilValue(widgetPositionSelector);
    // useKeenSlider;
    const [sliderRef, slider] = useKeenSlider({
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        loop: false,
        mode: 'snap',
        rtl: false,
        slides: { perView: 'auto', spacing: 8 },
        initial: 0,
    });
    const { open, getInputProps } = useFileDropzone();
    useEffect(() => {
        var _a, _b;
        if (slider) {
            // this is for calculating the right arrow position
            let width = 0;
            (_a = slider.current) === null || _a === void 0 ? void 0 : _a.slides.forEach((slide) => {
                width += slide.offsetWidth + 8;
            });
            setSlidesWidth(width);
            // update slider every time a file is uploaded
            (_b = slider.current) === null || _b === void 0 ? void 0 : _b.update();
        }
    }, [slider, filesToUploadLength]);
    // Disable the input for 1 second if there is a Virtual Agent present.
    // This gives time to the brain to respond to user input. This is a hacky but
    // if we don't do this, the user can type many times before the brain responds.
    const disableInputTimeout = useCallback(() => {
        let timer;
        if (shouldDisableInput) {
            setDisableInput(true);
            timer = setTimeout(() => {
                setDisableInput(false);
            }, MAX_TYPING_DURATION_MS);
        }
        return () => clearTimeout(timer);
    }, [shouldDisableInput]);
    useEffect(() => {
        if (isTyping) {
            setIsTyping(false);
            sendCompose('stop');
        }
        // We check `isTyping` because we don't want to send a `stop` event if the user was not typing.
        // Don't add it to the hook dependencies, it will cause an infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedInput, sendCompose]);
    // Set the focus on the element 50ms after is shown
    useEffect(() => {
        if (isWidgetOpen &&
            inputRef.current &&
            widgetPosition !== 'embed' &&
            filesToUploadLength === 0) {
            setTimeout(() => {
                var _a;
                (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 100);
        }
    }, [filesToUpload, filesToUploadLength, isWidgetOpen, widgetPosition]);
    const handleKeyEvent = useCallback((event) => {
        // In some cases, we are uploading a large file that takes time so we need to disable the "keyevent"
        if (isUploading) {
            return;
        }
        if ((filesToUploadLength > 0 || input.trim().length > 0) &&
            (event.key === KEYS.ENTER || event.type === 'click')) {
            event.preventDefault();
            sendMessage({
                text: input === null || input === void 0 ? void 0 : input.trim(),
                attachments: filesToUpload.map((f) => {
                    return {
                        type: getFileTypeFromMimeType(f.content_type),
                        id: f.file_id,
                        mime_type: f.content_type,
                        title: f.original_name,
                        filename: f.filename,
                    };
                }),
            });
            disableInputTimeout();
            if (inputRef.current) {
                inputRef.current.textContent = '';
                setInput('');
            }
            if (filesToUpload.length > 0) {
                setFilesToUpload([]);
            }
        }
    }, [
        input,
        isUploading,
        filesToUploadLength,
        sendMessage,
        filesToUpload,
        disableInputTimeout,
        setFilesToUpload,
    ]);
    const handleInputEvent = useCallback((event) => {
        handleKeyEvent(event);
    }, [handleKeyEvent]);
    /**
     * Prevents input/paste of more characters than allowed.
     * When deleting text , allow placeholder to be shown again in Firefox and Safari
     */
    const handleLimitInputChange = useCallback(() => {
        var _a, _b, _c;
        if (((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.textContent) &&
            ((_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.textContent.length) >= MAX_CHARS) {
            inputRef.current.textContent = (_c = inputRef.current) === null || _c === void 0 ? void 0 : _c.textContent.slice(0, MAX_CHARS);
            const selection = window.getSelection();
            const range = document.createRange();
            selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
            range.selectNodeContents(inputRef.current);
            range.collapse(false);
            selection === null || selection === void 0 ? void 0 : selection.addRange(range);
            inputRef.current.focus();
        }
        const element = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
        if (!(element === null || element === void 0 ? void 0 : element.textContent)) {
            const breakTag = element === null || element === void 0 ? void 0 : element.getElementsByTagName('br')[0];
            if (breakTag) {
                element === null || element === void 0 ? void 0 : element.removeChild(breakTag);
            }
        }
    }, []);
    const handleKeyUp = useCallback((event) => {
        if ((filesToUploadLength > 0 || input.trim().length > 0) &&
            event.key === KEYS.ENTER) {
            handleInputEvent(event);
        }
    }, [filesToUploadLength, handleInputEvent, input]);
    const handleInputChange = useCallback(() => {
        var _a;
        handleLimitInputChange();
        if (!isTyping) {
            sendCompose('start');
            setIsTyping(true);
        }
        setInput(((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.textContent) || '');
    }, [handleLimitInputChange, isTyping, sendCompose]);
    const handleKeyDown = useCallback((event) => {
        if (event.key === KEYS.ENTER) {
            event.preventDefault();
        }
    }, []);
    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text.trim());
    }, []);
    const handleAttachFileClick = useCallback(() => {
        open();
    }, [open]);
    const handleRemoveFile = useCallback((fileId) => {
        setFilesToUpload((prev) => prev.filter((file) => file.file_id !== fileId));
    }, [setFilesToUpload]);
    return (_jsxs("div", { className: cn(styles.innerContainer, styles[theme], {
            [styles.taller]: filesToUploadLength > 0,
        }), children: [_jsxs("div", { className: cn(styles.inputContainer, {
                    [styles.isMobile]: isMobile,
                }), children: [_jsx("div", { ref: inputRef, className: cn("message-input" /* TestClassName.MessageInput */, styles.input), "aria-label": "input", role: "textbox", tabIndex: 0, "data-placeholder": t('input_placeholder'), contentEditable: connectionStatus === 'online' && !disableInput, onKeyUp: handleKeyUp, onKeyDown: handleKeyDown, onInput: handleInputChange, onPaste: handlePaste }), _jsx("span", { className: styles.sendIcon, onKeyUp: handleInputEvent, onClick: handleInputEvent, role: "button", tabIndex: 0, children: disableInput ? (_jsx(LoadingIcon, { size: "small" })) : filesToUploadLength === 0 ? (_jsx(OldSendMessageIcon, { color: input.length > 0
                                ? 'var(--moveo-background-color)'
                                : 'var(--moveo-text-05)' })) : null }), filesToUploadLength == 0 && canUploadFile && (_jsxs(_Fragment, { children: [_jsx("input", Object.assign({}, getInputProps())), _jsx("span", { onKeyUp: handleAttachFileClick, onClick: handleAttachFileClick, onTouchStart: handleAttachFileClick, role: "button", tabIndex: 0, className: styles.attachFile, children: _jsx(FileIcon, { color: "var(--moveo-text-05)", size: 14 }) })] }))] }), _jsxs("div", { className: styles.secondLevel, children: [filesToUploadLength > 0 && (_jsx(FilesCarousel, { filesToUpload: filesToUpload, ref: sliderRef, currentSlide: currentSlide, slider: slider, handleRemoveFile: handleRemoveFile, slidesWidth: slidesWidth })), _jsx("span", { onKeyUp: handleInputEvent, onClick: handleInputEvent, role: "button", tabIndex: 0, className: cn(styles.sendMessage, {
                            [styles.show]: filesToUploadLength > 0,
                        }), title: t('input_send'), children: disableInput ? _jsx(LoadingIcon, { size: "small" }) : _jsx(OldSendMessageIcon, {}) })] })] }));
};
